import React, {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import {Link} from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";
import {baseUrl, userName} from "./Univ";
/* ============================================================
   DRGHIS ENTERPRISE HOSPITAL OVERVIEW
   SAP / ENTERPRISE MIS INSPIRED
   ============================================================ */
function Home() {
    const calendarRef = useRef();
    /* ============================================================
       DATE
       ============================================================ */
    const getLocalDate = () => {
        const d = new Date();
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };
    const today = getLocalDate();
    const currentDate = new Date().toLocaleDateString("en-IN", {
        day: "2-digit", month: "long", year: "numeric"
    });
    /* ============================================================
       USER
       ============================================================ */
    const isAdmin = localStorage.getItem("userGroup") === "Admin";
    /* ============================================================
       STATES
       ============================================================ */
    const [messview, setMessView] = useState([]);
    const [newsview, setNewsView] = useState([]);
    const [opData, setOpData] = useState([]);
    const [allAppointments, setAllAppointments] = useState([]);
    const [labcount, setLabCount] = useState(0);
    const [labcount1, setLabCount1] = useState(0);
    const [invcount, setInvCount] = useState(0);
    const [invcount1, setInvCount1] = useState(0);
    const [ipdCount, setIpdCount] = useState(0);
    const [ipdTodayCount, setIpdTodayCount] = useState(0);
    const [opCollection, setOpCollection] = useState([]);
    const [ipCollection, setIpCollection] = useState([]);
    const [refData, setRef] = useState([]);
    const [dueData, setDue] = useState([]);
    const [iprefData, setIpRef] = useState([]);
    const [events, setEvents] = useState([]);
    /* ============================================================
       COLLECTION DATA
       ============================================================ */
    async function fetchCollectionData() {
        try {
            const startDate = today;
            const endDate = today;
            const [opResponse, ipResponse, refResponse, dueResponse, iprefResponse] = await Promise.all([axios.get(`${baseUrl}all-collection-view/?start_date=${startDate}&end_date=${endDate}&cashier=&mode=&status=2`), axios.get(`${baseUrl}all-ip-collection-view/?start_date=${startDate}&end_date=${endDate}&cashier=&mode=&status=2`), axios.get(`${baseUrl}refund-report-view/?start_date=${startDate}&end_date=${endDate}`), axios.get(`${baseUrl}due-report-view/?start_date=${startDate}&end_date=${endDate}`), axios.get(`${baseUrl}ip-refund-report-view/?start_date=${startDate}&end_date=${endDate}`)]);
            setOpCollection(opResponse.data || []);
            setIpCollection(ipResponse.data || []);
            setRef(refResponse.data || []);
            setDue(dueResponse.data || []);
            setIpRef(iprefResponse.data || []);
        } catch (err) {
            console.log("Collection Error:", err);
        }
    }
    /* ============================================================
       OP APPOINTMENTS
       ============================================================ */
    async function fetchOpData() {
        try {
            const res = await axios.get(baseUrl + "op-appointment-view/");
            const data = res.data || [];
            setAllAppointments(data);
            const todayOpd = data.filter(r => r.appt_date === today);
            setOpData(todayOpd);
        } catch (err) {
            console.log("OP Error:", err);
        }
    }
    /* ============================================================
       MESSAGES
       ============================================================ */
    function fetchmessData() {
        axios
            .get(baseUrl + "view-mess/" + userName)
            .then(res => setMessView(res.data || []))
            .catch(err => console.log(err));
    }
    /* ============================================================
       NEWS
       ============================================================ */
    function fetchnewsData() {
        axios
            .get(baseUrl + "view-news/")
            .then(res => setNewsView(res.data || []))
            .catch(err => console.log(err));
    }
    /* ============================================================
       LAB
       ============================================================ */
    function fetchLabData() {
        axios
            .get(`${baseUrl}lab-entry-view/${today}/`)
            .then(res => setLabCount((res.data || []).length));
        axios
            .get(`${baseUrl}ip-lab-entry-view/${today}/`)
            .then(res => setLabCount1((res.data || []).length));
    }
    /* ============================================================
       RADIOLOGY / INVESTIGATION
       ============================================================ */
    function fetchInvData() {
        axios
            .get(`${baseUrl}inv-entry-view/${today}/`)
            .then(res => setInvCount((res.data || []).length));
        axios
            .get(`${baseUrl}ip-inv-entry-view/${today}/`)
            .then(res => setInvCount1((res.data || []).length));
    }
    /* ============================================================
       IPD
       ============================================================ */
    function fetchIpViewData() {
        axios
            .get(`${baseUrl}view-ip/?status=2`)
            .then(res => {
                const data = res.data || [];
                setIpdCount(data.length);
                const todayIpd = data.filter(r => {
                    if (!r.admit_date) {
                        return false;
                    }
                    return (r.admit_date.substring(0, 10) === today);
                });
                setIpdTodayCount(todayIpd.length);
            })
            .catch(err => console.log(err));
    }
    /* ============================================================
       CALENDAR
       ============================================================ */
    async function fetchEventsData() {
        try {
            const res = await axios.get(`${baseUrl}op-appointment-view/`);
            const data = res.data || [];
            const todayEvents = data.filter(e => e.appt_date === today);
            const formattedEvents = todayEvents.map(event => {
                const datetime = event.appt_date + "T" + event.appt_time;
                return {
                    id: event.id,
                    title: (event.cons__consultant || "Consultant") + " - " + (event.pat__patient || "Patient"),
                    start: datetime
                };
            });
            setEvents(formattedEvents);
        } catch (err) {
            console.log("Calendar Error:", err);
        }
    }
    /* ============================================================
       INITIAL LOAD
       ============================================================ */
    useEffect(() => {
        fetchmessData();
        fetchnewsData();
        fetchOpData();
        fetchLabData();
        fetchInvData();
        fetchIpViewData();
        fetchEventsData();
        fetchCollectionData();
    }, []);
    /* ============================================================
       COLLECTION CALCULATIONS
       ============================================================ */
    let TotalCons = 0;
    let TotalLab = 0;
    let TotalInv = 0;
    let TotalMin = 0;
    let TotalRef = 0;
    let TotalDue = 0;
    let TotalAdv = 0;
    let TotalIpLab = 0;
    let TotalIpInv = 0;
    let TotalIpRef = 0;
    opCollection.forEach(p => {
        TotalCons += Number(p.paid || 0);
        TotalLab += Number(p.lab_paid || 0);
        TotalInv += Number(p.inv_paid || 0);
        TotalMin += Number(p.min_paid || 0);
    });
    refData.forEach(p => TotalRef += Number(p.refund_amount || 0));
    dueData.forEach(p => TotalDue += Number(p.due_amount || 0));
    ipCollection.forEach(p => {
        TotalAdv += Number(p.paid || 0);
        TotalIpLab += Number(p.lab_paid || 0);
        TotalIpInv += Number(p.inv_paid || 0);
    });
    iprefData.forEach(p => TotalIpRef += Number(p.refund_amount || 0));
    /* ============================================================
       BASIC COUNTS
       ============================================================ */
    const opdCount = opData.length;
    /* ============================================================
       APPOINTMENT TYPES
       ============================================================ */
    const appointmentTypeCounts = opData.reduce((counts, appointment) => {
        if (appointment.type === "new") {
            counts.new++;
        } else if (appointment.type === "revisit") {
            counts.revisit++;
        }
        return counts;
    }, {
        new: 0, revisit: 0
    });
    /* ============================================================
       MINOR PROCEDURES
       ============================================================ */
    const minorCount = opCollection.reduce((total, item) => total + Number(item.min_paid ? 1 : 0), 0);
    /* ============================================================
       FINANCIAL TOTALS
       ============================================================ */
    const opTotal = TotalCons + TotalLab + TotalInv + TotalMin;
    const ipTotal = TotalAdv + TotalIpLab + TotalIpInv;
    const totalCollection = opTotal + ipTotal;
    const totalRefund = TotalRef + TotalIpRef;
    /* ============================================================
       LAST 7 DAYS OPD TREND
       ============================================================ */
    const opdTrend = useMemo(() => {
        const result = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date();
            d.setDate(d.getDate() - i);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            const date = `${year}-${month}-${day}`;
            const count = allAppointments.filter(x => x.appt_date === date).length;
            result.push({
                date: d.toLocaleDateString("en-IN", {
                    day: "2-digit", month: "short"
                }), opd: count
            });
        }
        return result;
    }, [allAppointments]);
    /* ============================================================
       CONSULTANT DISTRIBUTION
       ============================================================ */
    const consultantData = useMemo(() => {
        const map = {};
        opData.forEach(item => {
            const name = item.cons__consultant || "Unknown";
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, value]) => ({
                name, value
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [opData]);
    /* ============================================================
       PATIENT FLOW
       ============================================================ */
    const patientFlowData = [{
        name: "OPD", value: opdCount
    }, {
        name: "IPD", value: ipdTodayCount
    }, {
        name: "Lab", value: labcount + labcount1
    }, {
        name: "Radiology", value: invcount + invcount1
    }, {
        name: "Minor", value: minorCount
    }];
    /* ============================================================
       OP REVENUE MIX
       ============================================================ */
    const opRevenueData = [{
        name: "Consultation", value: TotalCons
    }, {
        name: "Laboratory", value: TotalLab
    }, {
        name: "Radiology", value: TotalInv
    }, {
        name: "Minor", value: TotalMin
    }];
    /* ============================================================
       IP REVENUE MIX
       ============================================================ */
    const ipRevenueData = [{
        name: "Advance", value: TotalAdv
    }, {
        name: "Laboratory", value: TotalIpLab
    }, {
        name: "Radiology", value: TotalIpInv
    }];
    /* ============================================================
       MONEY FORMAT
       ============================================================ */
    const money = value => {
        return Number(value || 0).toLocaleString("en-IN", {
            maximumFractionDigits: 0
        });
    };
    /* ============================================================
       RENDER
       ============================================================ */
    return (<div className="drhis-home">


        {/* ====================================================
               DASHBOARD HEADER
               ==================================================== */}

        <div className="dashboard-header">

            <div>

                <div className="dashboard-brand">
                    DrHiS • HOSPITAL MANAGEMENT
                </div>

                <h4 className="dashboard-title">
                    Hospital Overview
                </h4>

            </div>


            <div className="dashboard-header-right">

                <div className="dashboard-date">
                    {currentDate}
                </div>

                <div className="dashboard-live-text">
                    Real-time operational overview
                </div>

            </div>

        </div>


        {/* ====================================================
               CONTEXT BAR
               ==================================================== */}

        <div className="dashboard-context">

            <div className="row align-items-center">

                <div className="col-md-3">

                    <div className="context-label">
                        REPORTING PERIOD
                    </div>

                    <div className="context-value">
                        Today
                    </div>

                </div>


                <div className="col-md-3">

                    <div className="context-label">
                        OPERATIONAL STATUS
                    </div>

                    <div className="system-status">

                        <span className="status-dot"/>

                        System Operational

                    </div>

                </div>


                <div className="col-md-3">

                    <div className="context-label">
                        USER
                    </div>

                    <div className="context-value">
                        {userName || "Hospital User"}
                    </div>

                </div>


                <div className="col-md-3 text-end">

                        <span className="live-badge">
                            MIS • LIVE
                        </span>

                </div>

            </div>

        </div>


        {/* ====================================================
               KPI SECTION
               ==================================================== */}

        <SectionTitle
            title="Operational KPIs"
        />


        <div className="row g-3">

            <DashboardCard
                title="OPD Today"
                value={opdCount}
                subtitle="Appointments"
                icon="OP"
                accent="blue"
            />

            <DashboardCard
                title="New Patients"
                value={appointmentTypeCounts.new}
                subtitle="First visits"
                icon="NP"
                accent="green"
            />

            <DashboardCard
                title="Revisit Patients"
                value={appointmentTypeCounts.revisit}
                subtitle="Follow-up"
                icon="RV"
                accent="cyan"
            />

            <DashboardCard
                title="IPD Today"
                value={ipdTodayCount}
                subtitle="New admissions"
                icon="IP"
                accent="orange"
            />

            <DashboardCard
                title="Total IPD"
                value={ipdCount}
                subtitle="Current inpatients"
                icon="IN"
                accent="red"
            />

            <DashboardCard
                title="Laboratory"
                value={labcount + labcount1}
                subtitle="Tests today"
                icon="LB"
                accent="purple"
            />

            <DashboardCard
                title="Radiology"
                value={invcount + invcount1}
                subtitle="Investigations"
                icon="RD"
                accent="cyan"
            />

            <DashboardCard
                title="Minor Procedures"
                value={minorCount}
                subtitle="Procedures today"
                icon="MN"
                accent="orange"
            />

        </div>


        {/* ====================================================
               FINANCIAL KPI
               ==================================================== */}

        <SectionTitle
            title="Financial KPIs"
        />


        <div className="row g-3">

            <DashboardCard
                title="OP Collection"
                value={`₹ ${money(opTotal)}`}
                subtitle="OP revenue"
                icon="₹"
                accent="blue"
            />

            <DashboardCard
                title="IP Collection"
                value={`₹ ${money(ipTotal)}`}
                subtitle="IP revenue"
                icon="₹"
                accent="green"
            />

            <DashboardCard
                title="Total Collection"
                value={`₹ ${money(totalCollection)}`}
                subtitle="Gross collection"
                icon="₹"
                accent="teal"
                wide
            />

            <DashboardCard
                title="Refunds"
                value={`₹ ${money(totalRefund)}`}
                subtitle="Processed today"
                icon="RF"
                accent="red"
            />

            <DashboardCard
                title="Outstanding Due"
                value={`₹ ${money(TotalDue)}`}
                subtitle="Due amount"
                icon="DU"
                accent="orange"
            />

        </div>


        {/* ====================================================
               PERFORMANCE ANALYTICS
               ==================================================== */}

        <SectionTitle
            title="Hospital Performance Analytics"
            className="dashboard-section-spacing"
        />


        <div className="row g-3">


            {/* OPD TREND */}

            <div className="col-lg-8">

                <EnterpriseCard
                    title="OPD Volume Trend"
                    subtitle="Daily outpatient activity • Last 7 days"
                >

                    <div className="chart-container chart-large">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <LineChart
                                data={opdTrend}
                                margin={{
                                    top: 10, right: 20, left: 0, bottom: 5
                                }}
                            >

                                <CartesianGrid
                                    className="chart-grid"
                                    strokeDasharray="3 3"
                                />

                                <XAxis
                                    dataKey="date"
                                    className="chart-axis"
                                />

                                <YAxis
                                    allowDecimals={false}
                                    className="chart-axis"
                                />

                                <Tooltip/>

                                <Line
                                    className="chart-line-blue"
                                    type="monotone"
                                    dataKey="opd"
                                    name="OPD"
                                    strokeWidth={3}
                                    dot={{
                                        r: 4
                                    }}
                                    activeDot={{
                                        r: 6
                                    }}
                                />

                            </LineChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>


            {/* PATIENT FLOW */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="Today's Patient Flow"
                    subtitle="Activity across hospital services"
                >

                    <div className="chart-container chart-large">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <BarChart
                                data={patientFlowData}
                                margin={{
                                    top: 10, right: 10, left: -15, bottom: 5
                                }}
                            >

                                <CartesianGrid
                                    className="chart-grid"
                                    strokeDasharray="3 3"
                                />

                                <XAxis
                                    dataKey="name"
                                    className="chart-axis"
                                />

                                <YAxis
                                    allowDecimals={false}
                                    className="chart-axis"
                                />

                                <Tooltip/>

                                <Bar
                                    className="chart-bar-cyan"
                                    dataKey="value"
                                    name="Volume"
                                    radius={[4, 4, 0, 0]}
                                />

                            </BarChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               SECOND ANALYTICS ROW
               ==================================================== */}

        <div className="row g-3 dashboard-row-spacing">


            {/* OPD COMPOSITION */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="OPD Patient Composition"
                    subtitle="New versus revisit"
                >

                    <div className="chart-container chart-medium">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <PieChart>

                                <Pie
                                    data={[{
                                        name: "New", value: appointmentTypeCounts.new
                                    }, {
                                        name: "Revisit", value: appointmentTypeCounts.revisit
                                    }]}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="48%"
                                    outerRadius={90}
                                    innerRadius={45}
                                    label
                                >

                                    <Cell
                                        className="pie-blue"
                                    />

                                    <Cell
                                        className="pie-green"
                                    />

                                </Pie>

                                <Tooltip/>

                                <Legend/>

                            </PieChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>


            {/* OP REVENUE */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="OP Revenue Mix"
                    subtitle="Including minor procedures"
                >

                    <div className="chart-container chart-medium">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <BarChart
                                data={opRevenueData}
                                layout="vertical"
                                margin={{
                                    left: 20, right: 20
                                }}
                            >

                                <CartesianGrid
                                    className="chart-grid"
                                    strokeDasharray="3 3"
                                />

                                <XAxis
                                    type="number"
                                    className="chart-axis"
                                />

                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    className="chart-axis"
                                    width={85}
                                />

                                <Tooltip
                                    formatter={value => `₹ ${money(value)}`}
                                />

                                <Bar
                                    className="chart-bar-blue"
                                    dataKey="value"
                                    radius={[0, 4, 4, 0]}
                                />

                            </BarChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>


            {/* IP REVENUE */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="IP Revenue Mix"
                    subtitle="Inpatient collection composition"
                >

                    <div className="chart-container chart-medium">

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <PieChart>

                                <Pie
                                    data={ipRevenueData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="48%"
                                    outerRadius={90}
                                    innerRadius={45}
                                    label
                                >

                                    <Cell
                                        className="pie-green"
                                    />

                                    <Cell
                                        className="pie-purple"
                                    />

                                    <Cell
                                        className="pie-cyan"
                                    />

                                </Pie>

                                <Tooltip
                                    formatter={value => `₹ ${money(value)}`}
                                />

                                <Legend/>

                            </PieChart>

                        </ResponsiveContainer>

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               OPERATIONAL WORKSPACE
               ==================================================== */}

        <SectionTitle
            title="Operational Workspace"
            className="dashboard-section-spacing"
        />


        <div className="row g-3">


            {/* CALENDAR */}

            <div className="col-lg-8">

                <EnterpriseCard
                    title="Today's Appointment Schedule"
                    subtitle="Consultant and patient appointment timeline"
                >

                    <div className="calendar-wrapper">

                        <FullCalendar
                            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                            headerToolbar={{
                                left: "prev,next today",
                                center: "title",
                                right: "dayGridMonth,timeGridWeek,timeGridDay"
                            }}
                            initialView="timeGridDay"
                            timeZone="Asia/Kolkata"
                            events={events}
                            ref={calendarRef}
                            height="520px"
                        />

                    </div>

                </EnterpriseCard>

            </div>


            {/* CONSULTANT PERFORMANCE */}

            <div className="col-lg-4">

                <EnterpriseCard
                    title="Consultant Activity"
                    subtitle="Today's OPD appointments"
                >

                    <div className="consultant-list">

                        {consultantData.length === 0 ? (<EmptyState
                            text="No consultant activity"
                        />) : (consultantData.map((item, index) => (<div
                            key={index}
                            className="consultant-item"
                        >

                            <div className="consultant-header">

                                                <span className="consultant-name">
                                                    {item.name}
                                                </span>

                                <span className="consultant-count">
                                                    {item.value}
                                                </span>

                            </div>


                            <div className="consultant-progress">

                                <div
                                    className="consultant-progress-value"
                                    style={{
                                        "--progress": `${Math.min(100, (item.value / Math.max(1, consultantData[0]?.value)) * 100)}%`
                                    }}
                                />

                            </div>

                        </div>)))}

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               COMMUNICATION
               ==================================================== */}

        <SectionTitle
            title="Hospital Information Center"
            className="dashboard-section-spacing"
        />


        <div className="row g-3">


            {/* MESSAGES */}

            <div className="col-lg-6">

                <EnterpriseCard
                    title="Internal Communication"
                    subtitle="Messages and notifications"
                    action={<Link
                        to="/comp-mess/"
                        className="card-action-link"
                    >
                        View all
                    </Link>}
                >

                    <div className="communication-list">

                        {messview.length === 0 ? (<EmptyState
                            text="No new messages"
                        />) : (messview.map((m, i) => (<div
                            key={i}
                            className="communication-item"
                        >

                            <div className="communication-text">
                                {m.message}
                            </div>

                        </div>)))}

                    </div>

                </EnterpriseCard>

            </div>


            {/* NEWS */}

            <div className="col-lg-6">

                <EnterpriseCard
                    title="Hospital News & Announcements"
                    subtitle="Latest hospital information"
                >

                    <div className="communication-list">

                        {newsview.length === 0 ? (<EmptyState
                            text="No announcements"
                        />) : (newsview.map((n, i) => (<div
                            key={i}
                            className="news-item"
                        >

                            {n.attachment && (<img
                                src={n.attachment}
                                alt=""
                                className="news-image"
                            />)}

                            <div className="news-content">

                                <div className="news-heading">
                                    {n.heading}
                                </div>

                                <div className="news-description">
                                    {n.description}
                                </div>

                            </div>

                        </div>)))}

                    </div>

                </EnterpriseCard>

            </div>

        </div>


        {/* ====================================================
               ADMIN FINANCIAL CONTROL
               ==================================================== */}

        {isAdmin && (<>

            <SectionTitle
                title="Financial Control Center"
                className="dashboard-section-spacing"
            />


            <div className="row g-3">


                {/* OP FINANCIAL */}

                <div className="col-lg-6">

                    <EnterpriseCard
                        title="OP Collection Analysis"
                        subtitle="Today's OP financial distribution"
                    >

                        <div className="chart-container chart-financial">

                            <ResponsiveContainer
                                width="100%"
                                height="100%"
                            >

                                <ComposedChart
                                    data={[{
                                        name: "Consultation", amount: TotalCons
                                    }, {
                                        name: "Laboratory", amount: TotalLab
                                    }, {
                                        name: "Radiology", amount: TotalInv
                                    }, {
                                        name: "Minor", amount: TotalMin
                                    }, {
                                        name: "Due", amount: TotalDue
                                    }, {
                                        name: "Refund", amount: TotalRef
                                    }]}
                                >

                                    <CartesianGrid
                                        className="chart-grid"
                                        strokeDasharray="3 3"
                                    />

                                    <XAxis
                                        dataKey="name"
                                        className="chart-axis"
                                    />

                                    <YAxis
                                        className="chart-axis"
                                    />

                                    <Tooltip
                                        formatter={value => `₹ ${money(value)}`}
                                    />

                                    <Bar
                                        className="chart-bar-blue"
                                        dataKey="amount"
                                        radius={[4, 4, 0, 0]}
                                    />

                                    <Line
                                        className="chart-line-green"
                                        type="monotone"
                                        dataKey="amount"
                                        strokeWidth={2}
                                    />

                                </ComposedChart>

                            </ResponsiveContainer>

                        </div>

                    </EnterpriseCard>

                </div>


                {/* FINANCIAL SUMMARY */}

                <div className="col-lg-6">

                    <EnterpriseCard
                        title="Financial Summary"
                        subtitle="Today's hospital revenue control"
                    >

                        <div className="financial-summary">

                            <FinancialRow
                                label="OP Consultation"
                                value={TotalCons}
                            />

                            <FinancialRow
                                label="OP Laboratory"
                                value={TotalLab}
                            />

                            <FinancialRow
                                label="OP Radiology"
                                value={TotalInv}
                            />

                            <FinancialRow
                                label="Minor Procedures"
                                value={TotalMin}
                                highlight
                            />

                            <FinancialRow
                                label="IP Advance"
                                value={TotalAdv}
                            />

                            <FinancialRow
                                label="IP Laboratory"
                                value={TotalIpLab}
                            />

                            <FinancialRow
                                label="IP Radiology"
                                value={TotalIpInv}
                            />

                            <FinancialRow
                                label="Refunds"
                                value={-(TotalRef + TotalIpRef)}
                                danger
                            />


                            <div className="net-collection">

                                <div className="net-collection-label">
                                    Net Collection
                                </div>

                                <div className="net-collection-value">
                                    ₹{" "}
                                    {money(totalCollection - totalRefund)}
                                </div>

                            </div>

                        </div>

                    </EnterpriseCard>

                </div>

            </div>

        </>)}


        {/* ====================================================
               FOOTER
               ==================================================== */}

        <div className="dashboard-footer">

            DrHiS Hospital Intelligence System
            &nbsp;•&nbsp;
            Enterprise MIS Dashboard
            &nbsp;•&nbsp;
            {currentDate}

        </div>

    </div>);
}
/* ================================================================
   SECTION TITLE
   ================================================================ */
function SectionTitle({
                          title, className = ""
                      }) {
    return (<div
        className={`dashboard-section-title ${className}`}
    >

        <div className="section-title-marker"/>

        <span>
                {title}
            </span>

    </div>);
}
/* ================================================================
   ENTERPRISE CARD
   ================================================================ */
function EnterpriseCard({
                            title, subtitle, children, action
                        }) {
    return (<div className="enterprise-card">

        <div className="enterprise-card-header">

            <div>

                <div className="enterprise-card-title">
                    {title}
                </div>

                {subtitle && (<div className="enterprise-card-subtitle">
                    {subtitle}
                </div>)}

            </div>

            {action}

        </div>


        <div className="enterprise-card-body">

            {children}

        </div>

    </div>);
}
/* ================================================================
   KPI CARD
   ================================================================ */
function DashboardCard({
                           title, value, subtitle, icon, accent, wide
                       }) {
    return (<div
        className={wide ? "col-xl-3 col-lg-4 col-md-6" : "col-xl-2 col-lg-3 col-md-4 col-sm-6"}
    >

        <div
            className={`dashboard-kpi-card kpi-${accent}`}
        >

            <div className="kpi-accent"/>


            <div className="kpi-content">

                <div className="kpi-title">
                    {title}
                </div>

                <div className="kpi-value">
                    {value}
                </div>

                <div className="kpi-subtitle">
                    {subtitle}
                </div>

            </div>


            <div className="kpi-icon">
                {icon}
            </div>

        </div>

    </div>);
}
/* ================================================================
   FINANCIAL ROW
   ================================================================ */
function FinancialRow({
                          label, value, danger, highlight
                      }) {
    return (<div className="financial-row">

            <span
                className={highlight ? "financial-label highlight" : "financial-label"}
            >
                {label}
            </span>

        <span
            className={danger ? "financial-value danger" : highlight ? "financial-value highlight" : "financial-value"}
        >

                ₹{" "}
            {Math.abs(Number(value || 0)).toLocaleString("en-IN")}

            </span>

    </div>);
}
/* ================================================================
   EMPTY STATE
   ================================================================ */
function EmptyState({
                        text
                    }) {
    return (<div className="empty-state">
        {text}
    </div>);
}
export default Home;